import { Component, OnInit } from '@angular/core';

import { AppService } from '../../services/app.service';
import { ITableColumn } from '../../shared/table/table.component';
import { ThesisReportService } from '../../services/thesis-report.service';
import { IThesisReportListItem } from '../../models/ThesisReport';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ThesisReportSummaryComponent } from './summary.component';
import { ParameterService } from '../../services/parameter.service';
import { Utils } from '../../core/Utils';

interface IListItem extends IThesisReportListItem {
    fullName: string;
    program: string;
}

@Component({
    selector: 'app-thesis-report-list',
    templateUrl: './list.component.html'
})
export class ThesisReportListComponent implements OnInit {
    constructor(
        private app: AppService,
        private service: ThesisReportService,
        private parameters: ParameterService,
        private modal: NgbModal
    ) { }

    items: IListItem[] = [];
    activePeriods: string[] = [];
    isActive?: boolean = undefined;

    readonly canEdit = this.app.currentUser?.rights.includes('THESIS_REPORT.EDIT');
    readonly canDelete = this.app.currentUser?.rights.includes('THESIS_REPORT.ADMIN');

    readonly columns: ITableColumn[] = [
        { property: 'fullName', label: 'thesisReport_lblDoctor', sorts: true },
        { property: 'StudyProgramCode', label: 'thesisReport_lblStudyProgramSupervisor', sorts: true, cssClass: 'hidden-sm-down' },
        { property: 'Modified', label: 'thesisReport_lblPrepared', sorts: true, type: 'date', cssClass: 'hidden-sm-down' },
        { property: 'DvsRegDate', label: 'thesisReport_lblDvsRegNumber', sorts: true, type: 'date', cssClass: 'hidden-md-down' },
        { width: '1px' }
    ];

    readonly filters: {
        text?: string,
        doctor: { options: string[], value: string[] },
        program: { options: string[], value: string[] },
        supervisor: { options: string[], value: string[] }
    } = {
            doctor: { options: [], value: [] },
            program: { options: [], value: [] },
            supervisor: { options: [], value: [] }
        };

    private allItems: IListItem[] = [];

    ngOnInit() {
        const now = new Date();
        const year = now.getFullYear();

        this.app.addLoading(this.parameters.getValues()).subscribe(data => {
            let isActive = false;

            this.activePeriods = this.parameters.findValue(data, 'ThesisReportActivePeriods', value => {
                const pairs = value.split(',');
                const display: string[] = [];

                pairs.forEach(p => {
                    const [fromStr, toStr] = p.split('--');

                    if (Utils.isMonthDateBetween(now, fromStr, toStr)) {
                        isActive = true;
                    }

                    const fromParts = fromStr.split('-');
                    const toParts = toStr.split('-');
                    const fromMonth = +fromParts[0];
                    const toMonth = +toParts[0];

                    const from = new Date(year, fromMonth - 1, +fromParts[1])
                        .toLocaleString(this.app.currentLanguage, { month: 'long', day: 'numeric' });

                    const to = new Date(year, toMonth - 1, +toParts[1])
                        .toLocaleString(this.app.currentLanguage, { month: 'long', day: 'numeric' });

                    display.push(`${from} - ${to}`);
                });

                return display;
            });

            this.isActive = isActive;   
        });

        this.fetch();
    }

    delete(item: IThesisReportListItem) {
        this.app.confirm(this.app.translate('confirmItemDelete'), result => {
            if (!result) return;

            this.app.addLoading(this.service.delete(item.Id)).subscribe(() => {
                this.fetch();
            });
        });
    }

    filter() {
        const text = this.filters.text?.toLowerCase();
        const doctor = this.filters.doctor.value;
        const program = this.filters.program.value;
        const supervisor = this.filters.supervisor.value;

        if (text || doctor || program || supervisor) {
            this.items = this.allItems.filter(t => {
                if (doctor.length && !doctor.includes(t.fullName)) return false;
                if (program.length && !program.includes(t.program)) return false;
                if (supervisor.length && !supervisor.some(n => t.Supervisors.includes(n))) return false;
                if (text) {
                    if (t.fullName.toLowerCase().includes(text)) return true;
                    if (t.StudyProgramCode?.toLowerCase().includes(text)) return true;
                    if (t.StudyProgramName?.toLowerCase().includes(text)) return true;
                    if (t.Supervisors.some(n => n.toLowerCase().includes(text))) return true;
                    if (t.Upn?.toLowerCase().includes(text)) return true;
                    if (t.CertificateNumber?.toLowerCase().includes(text)) return true;
                    if ((t.DvsRegNumber || '').toString().includes(text)) return true;
                    return false;
                }
                return true;
            });
        } else {
            this.items = this.allItems;
        }
    }

    getSupervisorDisplay(item: IThesisReportListItem) {
        return item.Supervisors.join(', ');
    }

    openSummary() {
        this.modal.open(ThesisReportSummaryComponent);
    }

    private fetch() {
        this.app.addLoading(this.service.get()).subscribe(data => {
            const doctors: string[] = [];
            const programs: string[] = [];
            const supervisors: string[] = [];
            const items: IListItem[] = [];

            data.forEach(t => {
                const fullName = `${t.FirstName} ${t.LastName}`;
                const program = `[${t.StudyProgramCode}] ${t.StudyProgramName}`;

                items.push({
                    ...t,
                    fullName,
                    program
                });

                if (!doctors.includes(fullName)) doctors.push(fullName);
                if (!programs.includes(program)) programs.push(program);

                t.Supervisors.forEach(n => {
                    if (!supervisors.includes(n)) supervisors.push(n);
                });
            });

            doctors.sort((a, b) => a.localeCompare(b, this.app.currentLanguage));
            programs.sort((a, b) => a.localeCompare(b, this.app.currentLanguage));
            supervisors.sort((a, b) => a.localeCompare(b, this.app.currentLanguage));

            this.filters.doctor.options = doctors;
            this.filters.program.options = programs;
            this.filters.supervisor.options = supervisors;

            // drop obsolete values (may occur after deleting a report)
            this.filters.doctor.value = doctors.filter(t => this.filters.doctor.value.includes(t));
            this.filters.program.value = programs.filter(t => this.filters.program.value.includes(t));
            this.filters.supervisor.value = supervisors.filter(t => this.filters.supervisor.value.includes(t));

            this.allItems = items;

            this.filter();
        });
    }
}
